import { Box, Divider, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { Button } from "Components/_Shared/Buttons/Button";
import { ClientCardDetail } from "Components/_Shared/Client/ClientCardDetail";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { useGetAnalysis, useGetAnalysisClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisClientItemRelationType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useState } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { FamilyMember } from "./FamilyMember";
import { FamilyMemberDrawer } from "./FamilyMemberDrawer";

export const FamilyPane: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [clientId, setClientId] = useState<string>();
  const [clientRelationType, setClientRelationType] = useState<AnalysisClientItemRelationType>();

  const { t } = useResource();
  const { readOnly, canAddFamilyMember, versionId, analysisId } = useContext(AnalysisDetailContext);
  const { data: analysisData } = useGetAnalysis(analysisId, versionId);
  const clientGuid = analysisData?.clientGuid;

  const { data: clientRelationsData, isLoading } = useGetAnalysisClients(analysisId, versionId);

  const handleDrawerOpen = (clientId?: string, clientRelationType?: AnalysisClientItemRelationType) => {
    setClientId(clientId);
    setClientRelationType(clientRelationType);
    setDrawerOpen(true);
  };

  const handleClientDrawerOpen = () => {
    setClientRelationType(AnalysisClientItemRelationType.Client);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setClientId(undefined);
    setClientRelationType(undefined);
    setDrawerOpen(false);
  };

  return (
    <>
      <Stack spacing={Spacing.XXXL} pb={Spacing.XXL}>
        <Box display="flex" justifyContent="space-between">
          <Box>
            <Typography variant="h3">Rodina</Typography>
            <Typography variant="body2" color="secondary">
              Komplexní přehled a správa informací o rodinných příslušnících klienta.
            </Typography>
          </Box>
          <Button
            disabled={!canAddFamilyMember}
            tooltip={!readOnly && !canAddFamilyMember ? t(Resources.AnalysisDetail.Family.AddMemberOwnerOnly) : undefined}
            color="secondary"
            startAdornment={<AddIcon />}
            onClick={() => handleDrawerOpen()}
          >
            Přidat člena
          </Button>
        </Box>
        {isLoading && (
          <Stack width={640} spacing={Spacing.XXL}>
            <Skeleton variant="rounded" width={640} height={44} />
            <Skeleton variant="rounded" width={640} height={44} />
            <Skeleton variant="rounded" width={640} height={44} />
          </Stack>
        )}
        {clientRelationsData && (
          <Stack width={640} spacing={Spacing.XXL}>
            <Typography variant="subtitle1" fontWeight={600} mb={Spacing.XL}>
              Klient
            </Typography>
            {clientGuid && (
              <Box pl={Spacing.XL}>
                <ClientCardDetail
                  clientGuid={clientGuid}
                  rows={[]}
                  onClick={handleClientDrawerOpen}
                  legalRepresentationValid={analysisData?.clientLegalRepresentationValid}
                />
              </Box>
            )}

            {clientRelationsData.clients.length > 1 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={Spacing.XL}>
                    Členové rodiny
                  </Typography>
                  <Grid container spacing={Spacing.L} pl={Spacing.XL}>
                    {clientRelationsData.clients
                      .filter((client) => client.relationType === AnalysisClientItemRelationType.RelatedClient)
                      .map((client) => {
                        return (
                          <Grid size={6} key={client.guid}>
                            <FamilyMember
                              birthDate={client.birthDate || ""}
                              clientId={client.guid}
                              firstName={client.firstName}
                              lastName={client.surname}
                              clientRelationType={client.relationType}
                              onEdit={handleDrawerOpen}
                            />
                          </Grid>
                        );
                      })}
                  </Grid>
                </Box>
              </>
            )}
          </Stack>
        )}
      </Stack>
      {clientGuid && (
        <FamilyMemberDrawer
          clientId={clientId}
          clientGuid={clientGuid}
          clientRelationType={clientRelationType}
          open={drawerOpen}
          anchor="right"
          onClose={handleDrawerClose}
        />
      )}
    </>
  );
};
